/**
 * Read a File and return a base64 data URL, downsized to fit within
 * `maxDim` on the longest side and re-encoded as JPEG at `quality`.
 *
 * Why: phone-camera uploads are 4–8 MB; menus only display them at
 * <200px. Downsizing on the client cuts upload time, network cost,
 * and yields consistent crisp thumbnails (no Retina blur).
 */
export async function fileToResizedDataUrl(
  file: File,
  maxDim = 1280,
  quality = 0.88,
): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.readAsDataURL(file);
  });

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error("decode failed"));
    i.src = dataUrl;
  });

  const longest = Math.max(img.width, img.height);
  if (longest <= maxDim) {
    // Already small enough — keep original encoding to avoid quality loss.
    return dataUrl;
  }
  const scale = maxDim / longest;
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return dataUrl;
  // Better downscale quality on most browsers.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

/**
 * Append ImageKit URL transformations so we deliver an appropriately
 * sized variant rather than the full original. No-op for non-IK URLs.
 */
export function ikThumb(url: string | null | undefined, w: number, h?: number): string {
  if (!url) return "";
  if (!/imagekit\.io|ik\.imagekit\.io/.test(url)) return url;
  const tr = h ? `w-${w},h-${h},fo-auto,c-maintain_ratio` : `w-${w},fo-auto`;
  return url.includes("?") ? `${url}&tr=${tr}` : `${url}?tr=${tr}`;
}
